import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { alarmsAPI } from '../../../main/platform/dashboards/alarms/ducks';
import { analyticsAPI } from '../../../main/platform/dashboards/overview/ducks';
import { getTypeIdFromPath, nodeTypes } from '../../../main/utilities/NodeClassifier';
import { RootState } from '../../types';
import { OPEN_ASSET_DETAIL, pollGroupConditions } from './fleetStatus';
import { getTree } from './getTree';

/**
 * The machine a health-changing operation belongs to. Reviews, interventions and alarm operations
 * are addressed to the machine, one of its measurement points or a single sensor, but the backend
 * always recomputes the whole machine subtree. Undefined for a node above machine level or one the
 * tree hasn't loaded.
 */
export const machineOf = (treeMap: any, nodeId: number | string | null | undefined): number | undefined => {
    if (nodeId == null) return undefined;
    const node = treeMap?.[nodeId];
    if (!node) return undefined;
    if (node.type === nodeTypes.machine) return node.id;
    const machineId = getTypeIdFromPath(node, nodeTypes.machine, treeMap);
    return machineId == null ? undefined : Number(machineId);
};

/**
 * Re-read a machine's condition after an operation that changed it.
 *
 * Saving a review, closing an incident, creating an intervention, any alarm operation, a manual
 * health change and ingesting an offline report all recompute the machine and its measurement
 * points inside the same request, so a follow-up read shows the new condition immediately — there
 * is nothing to poll or retry.
 *
 * Two reads, because health is stored in two places:
 *  - `fleet_status` carries the machine's own condition into the `status` slice, which every card,
 *    tree row and drawer reads. It asks for the asset drawer's field set unconditionally: this is a
 *    single machine, so covering whichever view is on screen costs one small payload.
 *  - the machine subtree carries the `data.health` of its points and sensors into `treeMap`. Read
 *    silently: this refreshes a view that is already on screen, so it must not raise `loadingTree`
 *    and drop the dashboard behind a full-page spinner.
 *
 * The group and plant health counts aggregate the machine's condition, so they go stale too.
 *
 * So does the alarms API. The condition matrix in the asset drawer takes the WORST of a point's
 * refreshed `data.health` and the band the variable table reports per variable, so leaving that
 * query cached pins a cell to the old value whenever the condition improves — disarming an alarm,
 * deleting a trigger, lowering the condition by hand. The alarm list goes with it: it is the
 * matrix's column set, and a manual condition change is carried out by resetting alarm triggers.
 * Neither cache is invalidated anywhere else — the legacy alarm thunks are plain axios and never
 * reach RTK Query, and `configureAlarms` has no `invalidatesTags`.
 */
export const refreshNodeHealth =
    (nodeId: number | string | null | undefined): ThunkAction<void, RootState, undefined, AnyAction> =>
    (dispatch, getState) => {
        const machineId = machineOf(getState().nodeTreeReducers.treeMap, nodeId);
        if (machineId === undefined) return;
        dispatch(pollGroupConditions(machineId, OPEN_ASSET_DETAIL));
        dispatch(getTree(machineId, null, { silent: true }));
        dispatch(analyticsAPI.util.invalidateTags(['MachineHealthCount']));
        dispatch(alarmsAPI.util.invalidateTags([{ type: 'VariableTable', id: machineId }, 'Alarm']));
    };
